import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'

// IST Date helpers - DB dates are already in IST, parse directly without timezone conversion
// This prevents browser from converting to local timezone (which shifts dates for US users)

// Format a date string to display format (DD/MM/YYYY) - parses date parts directly
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    // Extract date parts directly from string to avoid timezone conversion
    const dateOnly = dateString.split('T')[0] // Handle ISO format "2024-01-15T00:00:00"
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

// Convert a date string to YYYY-MM-DD format for form inputs - parses directly
const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    // Extract date parts directly from string to avoid timezone conversion
    const dateOnly = dateString.split('T')[0] // Handle ISO format
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    // Handle other formats if needed
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

type TopupPlan = {
  name: string
  sumInsured: number
  premium: number
}

const RELATIONS = [
  'Spouse',
  'Child',
  'Parent'
]

const GENDERS = ['Male', 'Female', 'Other']

const GST_RATE = 0.18

const TOPUP_PLANS: TopupPlan[] = [
  { name: 'Topup300000', sumInsured: 300000, premium: 7200 },
  { name: 'Topup500000', sumInsured: 500000, premium: 9350 },
  { name: 'Topup700000', sumInsured: 700000, premium: 13800 },
  { name: 'Topup1000000', sumInsured: 1000000, premium: 23100 }
]

const getAgeAsOfDate = (dob: string, asOfDate: string) => {
  const birthDate = new Date(dob)
  const refDate = new Date(asOfDate)
  if (Number.isNaN(birthDate.getTime()) || Number.isNaN(refDate.getTime())) return 0
  
  // Calculate base age from year difference
  let age = refDate.getFullYear() - birthDate.getFullYear()
  
  // Adjust for whether birthday has occurred in the reference year
  const birthdayThisYear = new Date(refDate.getFullYear(), birthDate.getMonth(), birthDate.getDate())
  
  if (refDate < birthdayThisYear) {
    // Birthday hasn't occurred yet this year
    age--
  } else if (refDate > birthdayThisYear) {
    // Birthday has passed - even 1 day after counts as next age
    age++
  }
  // If exactly on birthday, age stays as calculated
  
  return age
}

export const PaltechbaseEnrollmentForm: React.FC<Props> = ({ policyData, policyEffectiveDate, policyExpiryDate, onSuccess, preloadedMembers, initialDependents }) => {
  // Form for enrolling family members
  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedTopup, setSelectedTopup] = useState<TopupPlan | null>(null)
  const [hasSelectedTopup, setHasSelectedTopup] = useState(false)
  const [preloadedTopup, setPreloadedTopup] = useState<{TopUp_SumInsured: number, TopUpPremiumE: number, TopUpGSTE: number} | null>(null)
  const [loadingTopup, setLoadingTopup] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  // New dependent form state
  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: ''
  })

  // Grade-to-SumInsured mapping
  const GRADE_SUM_INSURED: Record<string, number> = {
    'Grade1': 100000,
    'Grade2': 300000,
    'Grade3': 400000,
    'Grade4': 500000
  }

  const employeeGrade = policyData.Grade || 'Grade1'
  const baseSumInsured = GRADE_SUM_INSURED[employeeGrade] || 100000

  // Grade1 allows only Self (employee), no dependents
  const isGrade1 = employeeGrade === 'Grade1'

  // Total dependents excluding self (Spouse + 2 Children + 2 Parents)
  const maxTotalDependents = isGrade1 ? 0 : 5

  // Use whichever is latest between PolicyEffectiveDate and DOJ for age calculation
  const getLatestDate = () => {
    const policyDate = policyEffectiveDate ? new Date(policyEffectiveDate) : null
    const dojDate = policyData.DOJ ? new Date(policyData.DOJ) : null
    
    if (policyDate && dojDate) {
      return policyDate > dojDate ? policyEffectiveDate! : policyData.DOJ
    }
    return policyDate ? policyEffectiveDate! : (dojDate ? policyData.DOJ : new Date().toISOString())
  }
  
  const ageReferenceDate = getLatestDate()

  // ─── Pro-rata helpers (same as ACS Global / Onity pattern) ────────────────
  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const getProrationFactor = (): number => {
    // Resolve DOJ: prefer JoiningDate from initialDependents, fallback to policyData.DOJ
    let dojRaw: any = undefined
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) { dojRaw = row.JoiningDate; break }
        if (row?.joiningdate != null) { dojRaw = row.joiningdate; break }
        if (row?.joiningDate != null) { dojRaw = row.joiningDate; break }
      }
    }
    if (dojRaw == null) dojRaw = policyData.DOJ

    const dojMs = parseDateOnlyToUtcMs(dojRaw)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    // Only new joiners (DOJ strictly after policy start) are pro-rated
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()
  const isProrated = prorationFactor < 1
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // If preloaded members are passed (Frozen state from parent), use them directly
    if (preloadedMembers && preloadedMembers.length > 0) {
        setEnrolledMembers(preloadedMembers)
        setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
        // Map initialDependents to form state
        const mappedDependents = initialDependents.map((item, index) => {
            let relation = item.Relation
            const rLower = relation ? relation.toLowerCase() : ''
            
            if (['husband', 'wife', 'spouse'].includes(rLower)) relation = 'Spouse'
            else if (['son', 'daughter', 'child'].includes(rLower)) relation = 'Child'
            else if (['father', 'mother', 'parent', 'father-in-law', 'mother-in-law'].includes(rLower)) relation = 'Parent'
            
            return {
                id: item.sid || Date.now() + index,
                name: item.Name,
                dob: item.DOB ? toISTInputFormat(item.DOB) : '',
                relation: relation,
                gender: item.Gender || ''
            }
        }).filter((dep) => dep.relation === 'Self' || dep.relation === 'Spouse' || dep.relation === 'Child' || dep.relation === 'Parent')
        setDependents(mappedDependents)
    } else {
        // Initialize with Employee as first dependent (Active state)
        setDependents([{
          id: 1, // Fixed ID for employee
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '' 
        }])
    }
  }, [policyData, preloadedMembers, initialDependents])

  // Preload top-up data from API
  useEffect(() => {
    const fetchTopupData = async () => {
      if (!policyData.Email || !policyData.CorporateCode) return
      setLoadingTopup(true)
      try {
        const response = await fetch('https://etl.futurisk.in/webhook/23159409-2334-412d-aacc-1dcc87dfb8723', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Email: policyData.Email,
            CorporateCode: policyData.CorporateCode,
          }),
        })
        if (response.ok) {
          const data = await response.json()
          const topupRecord = Array.isArray(data) ? data[0] : data
          if (topupRecord && topupRecord.TopUp_SumInsured) {
            setPreloadedTopup({
              TopUp_SumInsured: topupRecord.TopUp_SumInsured,
              TopUpPremiumE: topupRecord.TopUpPremiumE,
              TopUpGSTE: topupRecord.TopUpGSTE,
            })
            // Auto-select the matching plan if available
            const matchingPlan = TOPUP_PLANS.find(p => p.sumInsured === topupRecord.TopUp_SumInsured)
            if (matchingPlan) {
              setSelectedTopup(matchingPlan)
              setHasSelectedTopup(true)
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch topup data:', err)
      } finally {
        setLoadingTopup(false)
      }
    }

    if (!policyData.freezeEnrolment) {
      fetchTopupData()
    }
  }, [policyData.Email, policyData.CorporateCode, policyData.freezeEnrolment])

  const calculatePremium = () => {
    const dependentPremium = 0
    const dependentCount = 0

    // Apply pro-rata to topup premium (new joiners only; factor = 1 for full-year members)
    const rawTopupPremium = selectedTopup ? selectedTopup.premium : 0
    const topupPremium = Math.round(rawTopupPremium * prorationFactor)
    
    const dependentGST = dependentPremium * GST_RATE
    const dependentTotal = dependentPremium + dependentGST
    
    const topupGST = Math.round(topupPremium * GST_RATE)
    const topupTotal = topupPremium + topupGST

    const totalTaxable = dependentPremium + topupPremium
    const totalGST = dependentGST + topupGST
    const totalPayable = totalTaxable + totalGST

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      topupPremium,
      topupGST,
      topupTotal,
      gst: totalGST,
      total: totalPayable
    }
  }

  const handleAddClick = () => {
    setError(null)
    const familyDependents = dependents.filter(d => d.relation !== 'Self')
    
    if (isGrade1) {
      setError('Grade 1 allows only the employee. No dependents can be added.')
      return
    }

    if (familyDependents.length >= maxTotalDependents) {
      setError(`Maximum of ${maxTotalDependents} dependents allowed.`)
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: ''
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (isGrade1) {
      setError('Grade 1 allows only the employee. No dependents can be added.')
      return false
    }

    if (!RELATIONS.includes(dep.relation)) {
      setError('Only Spouse, Child, and Parent relations are allowed.')
      return false
    }

    const age = getAgeAsOfDate(dep.dob, ageReferenceDate)

    // Age validations
    if (dep.relation === 'Child' && age >= 25) {
      setError('Child must be below 25 years of age.')
      return false
    }

    // Exclude the currently-editing dependent from counts
    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    // Relation count validations
    const relationCounts = otherDeps.reduce((acc, curr) => {
      acc[curr.relation] = (acc[curr.relation] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    if (dep.relation === 'Spouse' && relationCounts['Spouse']) {
      setError('Only 1 Spouse allowed.')
      return false
    }

    if (dep.relation === 'Child' && (relationCounts['Child'] || 0) >= 2) {
      setError('Maximum 2 Children allowed.')
      return false
    }

    if (dep.relation === 'Parent' && (relationCounts['Parent'] || 0) >= 2) {
      setError('Maximum 2 Parents allowed.')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    if (editingDependentId) {
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    setDependents(dependents.filter(d => d.id !== id))
    setError(null)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()
    
    // Map premium against each family member
    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: 0,
        base_premium: 0,
        Grade: policyData.Grade,
        crm_client_id: policyData.CorpCode,
        Email:  currentUser?.Email,
        CorporateCode: policyData.CorporateCode
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          SumInsured: baseSumInsured,
          premiumOpted: Boolean(selectedTopup),
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,
          
          // Topup details
          TopupName: selectedTopup?.name || '',
          topupsumInsured: selectedTopup?.sumInsured || 0,
          topup_base_premium: premiumData.topupPremium,
          topup_gst: premiumData.topupGST,
          topup_total_premium: premiumData.topupTotal,

          // Base (Dependent) details
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          // Grand totals
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      return baseMember
    })

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch('https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(familyMembersWithPremium),
      })

      if (response.ok) {
         setShowSuccess(true)
      } else {
         setError('Failed to submit enrollment. Please try again.')
         setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()
  const availableTopups = isGrade1 ? [] : TOPUP_PLANS

  if (policyData.freezeEnrolment) {
    // If explicitly frozen or if we have preloaded members (which implies frozen/submitted state)
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0)) return <div className="text-center p-5 text-primary"><span className="spinner-border spinner-border-sm align-middle ms-2"></span> Loading enrollment details...</div>
    
    // Calculate totals from enrolled data (either from prop or state)
    const membersToDisplay = (preloadedMembers && preloadedMembers.length > 0) ? preloadedMembers : enrolledMembers
    
    const frozenPremium = {
        dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
        topupPremium: membersToDisplay.reduce((acc, m) => acc + (m.TopupBasePremium || 0), 0),
        gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
        total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    const optedTopupMember = membersToDisplay.find(m => m.TopupName)
    const optedTopup = optedTopupMember?.TopupName
    const optedTopupSI = optedTopupMember?.TopupSumInsured

    return (
        <div className='card'>
            <div className='card-body'>
                <Row>
                    <Col md={8}>
                       <div className="d-flex justify-content-between align-items-center mb-4">
                           <h3 className="mb-0">Enrolled Family Details</h3>
                           {baseSumInsured > 0 && <div className="badge badge-light-primary fs-6 p-3">Family Sum Insured: ₹{baseSumInsured.toLocaleString()}</div>}
                       </div>
                       <div className='table-responsive mb-5'>
                         <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                           <thead>
                             <tr className='fw-bolder text-muted'>
                               <th>Name</th>
                               <th>Relation</th>
                               <th>Gender</th>
                               <th>Date of Birth</th>
                             </tr>
                           </thead>
                           <tbody>
                             {membersToDisplay.map((member) => (
                               <tr key={member.Id}>
                                 <td>
                                    <div className='d-flex align-items-center'>
                                        <div className='d-flex justify-content-start flex-column'>
                                            <div className='text-dark fw-bolder fs-6'>
                                            {member.Name}
                                            </div>
                                        </div>
                                    </div>
                                 </td>
                                 <td>{member.Relation}</td>
                                 <td>{member.Gender}</td>
                                 <td>{formatDateIST(member.Dob)}</td>
                               </tr>
                             ))}
                           </tbody>
                         </Table>
                       </div>
                       
                       {availableTopups.length > 0 && (
                           <>
                                <h3 className="mb-3">Selected Top-up Plan</h3>
                                <div className="alert alert-info d-flex align-items-center mb-3">
                                   <i className="bi bi-info-circle-fill text-info fs-3 me-3"></i>
                                   <div className="text-gray-700 fw-bold fs-7">
                                      Top-Up coverage provides additional sum insured over and above base family for enrolled members
                                   </div>
                                </div>
                                <div className="p-4 bg-light rounded border border-gray-300">
                                    {optedTopup ? (
                                        <div className="fw-bold fs-5 text-gray-800">
                                            {optedTopup} {optedTopupSI ? `- Suminsured : ₹${optedTopupSI.toLocaleString()}` : ''}
                                        </div>
                                    ) : (
                                        <div className="text-muted">No Top-up plan selected.</div>
                                    )}
                                </div>
                            </>
                       )}
                    </Col>
                    <Col md={4}>
                        <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                           <h4 className='mb-4'>Premium Summary</h4>
                           
                           {frozenPremium.dependentPremium > 0 && (
                             <div className='mb-3'>
                               <strong>Additional Dependents Premium:</strong>
                               <div className='float-end'>₹{frozenPremium.dependentPremium.toLocaleString()}</div>
                             </div>
                           )}
                           {frozenPremium.topupPremium > 0 && (
                             <div className='mb-3'>
                               <strong>Top-up Premium:</strong>
                               <div className='float-end'>₹{frozenPremium.topupPremium.toLocaleString()}</div>
                             </div>
                           )}
                           
                           {(frozenPremium.total > 0) ? (
                            <>
                              <hr />
                              <div className='mb-2'>
                                <span>Taxable Amount:</span>
                                <span className='float-end'>₹{(frozenPremium.dependentPremium + frozenPremium.topupPremium).toLocaleString()}</span>
                              </div>
                              <div className='mb-2'>
                                <span>GST (18%):</span>
                                <span className='float-end'>₹{frozenPremium.gst.toLocaleString()}</span>
                              </div>
                              <hr />
                              <h5 className='text-primary'>
                                Total Payable:
                                <span className='float-end'>₹{frozenPremium.total.toLocaleString()}</span>
                              </h5>
                              <div className="text-muted fs-7 mt-2 text-end">
                                The total premium will be deducted from your next salary.
                              </div>
                            </>
                           ) : (
                             <div className='text-muted text-center py-5'>
                               No additional premium applicable.
                             </div>
                           )}
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    )
  }

  if (showSuccess) {
    return (
       <div className='card'>
        <div className='card-body text-center py-5'>
           <div className="mb-4">
              <i className="bi bi-check-circle-fill text-success fs-3x"></i>
           </div>
           <h2 className="text-success mb-4">Thank You!</h2>
           
           {premiumInfo.total > 0 ? (
             <div className="alert alert-info d-inline-block p-4">
                <span className="fs-4">
                   An amount of <strong>₹{premiumInfo.total.toLocaleString()}</strong> will be deducted from your salary.
                </span>
             </div>
           ) : (
             <div className="fs-4 text-gray-600">
                Your enrollment is complete.
             </div>
           )}
           
           {/* <div className="mt-5">
             <Button variant='primary' size="lg" onClick={onSuccess}>Close</Button>
           </div> */}
        </div>
       </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
                {error}
            </div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                  <h3 className="mb-0">Family Details</h3>
                  {baseSumInsured > 0 && <div className="text-primary fw-bold mt-1">Family Sum Insured: ₹{baseSumInsured.toLocaleString()}</div>}
              </div>
              {!isGrade1 && (
                <Button variant='primary' onClick={handleAddClick} disabled={dependents.filter(d => d.relation !== 'Self').length >= maxTotalDependents}>
                  Add Dependent
                </Button>
              )}
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => {
                    return (
                      <tr key={dep.id} className='text-capitalize text-dark'>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                                {dep.name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {dep.relation}
                          </span>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {dep.gender}
                          </span>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {formatDateIST(dep.dob)}
                          </span>
                        </td>
                        <td>
                          {dep.relation !== 'Self' && (
                            <div className='d-flex gap-2'>
                              <Button variant='primary' size='sm' onClick={() => handleEditClick(dep)}>
                                Edit
                              </Button>
                              <Button variant='danger' size='sm' onClick={() => removeDependent(dep.id)}>
                                Remove
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {availableTopups.length > 0 && (
              <div className='mb-5'>
                

                <h3>Optional Top-up Plans</h3>

                <div className="alert alert-warning d-flex align-items-center mb-4">
                     <i className="bi bi-info-circle-fill text-warning fs-3 me-3"></i>
                     <div className="text-gray-700 fw-bold">
                         Top-Up coverage provides additional sum insured for you and your family at an extra cost, if you choose this option, the total premium will be deducted from your next salary.
                     </div>
                </div>

                {loadingTopup && (
                  <div className='text-center mb-3'>
                    <span className='spinner-border spinner-border-sm text-primary me-2' role='status'></span>
                    <span className='text-muted'>Loading top-up details...</span>
                  </div>
                )}

                <Form.Group>
                  <Form.Check
                    type='radio'
                    label='No Top-up'
                    name='topupPlan'
                    id='topup-none'
                    checked={hasSelectedTopup && selectedTopup === null}
                    onChange={() => { setSelectedTopup(null); setHasSelectedTopup(true) }}
                    className='mb-2'
                  />
                  {availableTopups.map((plan, idx) => {
                    const isPreloaded = preloadedTopup && preloadedTopup.TopUp_SumInsured === plan.sumInsured
                    return (
                        <div key={idx} className='d-flex align-items-center mb-2'>
                          <Form.Check
                            type='radio'
                            label={
                              <span>
                                {`${plan.name} (Sum Insured: ₹${plan.sumInsured.toLocaleString()}) - Premium: ₹${plan.premium.toLocaleString()}`}
                                {isPreloaded && <span className='badge badge-light-success ms-2'>Previously Selected</span>}
                              </span>
                            }
                            name='topupPlan'
                            id={`topup-${idx}`}
                            checked={hasSelectedTopup && selectedTopup?.name === plan.name}
                            onChange={() => { setSelectedTopup(plan); setHasSelectedTopup(true) }}
                          />
                        </div>
                    )
                  })}
                </Form.Group>
              </div>
            )}
          </Col>

          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>
              
              {premiumInfo.dependentCount > 0 && (
                <div className='mb-3'>
                  <strong>Additional Dependents ({premiumInfo.dependentCount}):</strong>
                  <div className='float-end'>₹{premiumInfo.dependentPremium.toLocaleString()}</div>
                </div>
              )}

              {selectedTopup && (
                <div className='mb-3'>
                  <strong>Top-up Premium:</strong>
                  <div className='float-end'>₹{premiumInfo.topupPremium.toLocaleString()}</div>
                </div>
              )}

              {isProrated && selectedTopup && (
                <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                  <span className='badge badge-warning me-2'>Pro-rated</span>
                  <span className='text-dark fs-7'>
                    Premium calculated for <strong>{Math.round(prorationFactor * 365)} days</strong> remaining in the policy year (factor: {(prorationFactor * 100).toFixed(1)}%).
                  </span>
                </div>
              )}

              {(premiumInfo.dependentCount > 0 || selectedTopup) ? (
                <>
                  <hr />
                  <div className='mb-2'>
                    <span>Taxable Amount:</span>
                    <span className='float-end'>₹{(premiumInfo.dependentPremium + premiumInfo.topupPremium).toLocaleString()}</span>
                  </div>
                  <div className='mb-2'>
                    <span>GST (18%):</span>
                    <span className='float-end'>₹{premiumInfo.gst.toLocaleString()}</span>
                  </div>
                  <hr />
                  <h5 className='text-primary'>
                    Total Payable:
                    <span className='float-end'>₹{premiumInfo.total.toLocaleString()}</span>
                  </h5>
                  <div className="text-muted fs-7 mt-2 text-end">
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  No additional premium applicable.
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button 
                  variant='success' 
                  size='lg' 
                  onClick={() => setShowConfirmModal(true)} 
                  disabled={isSubmitting || (availableTopups.length > 0 && !hasSelectedTopup)}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
                {availableTopups.length > 0 && !hasSelectedTopup && (
                  <div className='text-muted text-center fs-7 mt-1'>Please select a top-up option to proceed</div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add Dependent Modal */}
      <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setEditingDependentId(null); setError(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingDependentId ? 'Edit Dependent' : 'Add Dependent'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
                {error}
            </div>
          )}
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type='text' 
                value={newDependent.name} 
                onChange={(e) => setNewDependent({...newDependent, name: e.target.value})}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select 
                value={newDependent.relation}
                onChange={(e) => setNewDependent({...newDependent, relation: e.target.value})}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select 
                value={newDependent.gender}
                onChange={(e) => setNewDependent({...newDependent, gender: e.target.value})}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control 
                type='date' 
                value={newDependent.dob} 
                onChange={(e) => setNewDependent({...newDependent, dob: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => { setShowAddModal(false); setEditingDependentId(null) }}>Cancel</Button>
          <Button variant='primary' onClick={saveDependent}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Final Verification Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning d-flex align-items-center">
             <span className="svg-icon svg-icon-2hx svg-icon-warning me-4">
                 <i className="bi bi-exclamation-triangle-fill fs-2"></i>
             </span>
             <div className="d-flex flex-column">
                 {premiumInfo.total > 0 && (
                   <>
                     <div className="fw-bold mb-1">Total Premium Deduction: ₹{premiumInfo.total.toLocaleString()}</div>
                     <div className="small">This amount will be debited from your salary.</div>
                   </>
                 )}
                 <div className="small mt-1"><strong>Please review and confirm your nominations carefully. Once submitted, no changes can be made in the policy year.</strong></div>
             </div>
          </div>
          <p className="mb-0 mt-3">Do you want to proceed to enroll your family?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button variant='primary' onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
          }} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Family'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
